import { FORM_ERROR } from "final-form"
import { StaticImage } from "gatsby-plugin-image"
import { Fragment } from "react"
import { Field, Form } from "react-final-form"

import { useMutation, userClient } from "@trueskin-web/apis"
import {
  Button,
  CheckboxControl,
  FieldError,
  Heading,
  Icon,
} from "@trueskin-web/components"
import { getErrorMessage, mustBeTrue } from "@trueskin-web/functions"
import { plusCircleIcon } from "@trueskin-web/theme/icons/plusCircleIcon"
import { Trans, useTranslation } from "@trueskin-web/translations"

import { PhotoSlots, PhotosProvider, usePhotos } from "../image-upload"

const UploadPhotosButton = (props) => (
  <Button
    data-testid="web-portal-submit-photos-btn"
    type="submit"
    isFullWidth
    {...props}
  >
    <Trans i18nKey="PhotosUpload.ImageUploadContainer.submitButton" />
  </Button>
)

const TelemedicineAgreementForm = ({ onSubmit, isDisabled, isLoading }) => (
  <Form
    onSubmit={onSubmit}
    render={({ handleSubmit, valid, submitError }) => (
      <form onSubmit={handleSubmit}>
        <Field
          dataTestId="web-portal-telemedicine-agreement-checkbox"
          name="telemedicine"
          component={CheckboxControl}
          size={["md", "sm"]}
          type="checkbox"
          validate={mustBeTrue}
          sx={{ mb: 6 }}
        >
          <Trans i18nKey="PhotosUpload.ImageUploadContainer.consentCheckbox" />
        </Field>

        {submitError && <FieldError sx={{ my: 4 }}>{submitError}</FieldError>}

        <UploadPhotosButton
          isDisabled={isDisabled || !valid}
          isLoading={isLoading}
        />
      </form>
    )}
  />
)

const Considerations = () => {
  const { t } = useTranslation()

  const considerations =
    t("PhotosUpload.considerations", {
      returnObjects: true,
    }) || []

  return (
    <div sx={{ mb: [7, 0] }}>
      <Heading as="h4" size="xsm" mb={[4, 5]}>
        {t("PhotosUpload.considerationsTitle")}
      </Heading>

      <ul
        sx={{
          display: "grid",
          gap: 3,
          my: 0,
          pl: 0,
          listStyle: "none",
        }}
      >
        {considerations.map((consideration, index) => (
          <li
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 2,
              fontSize: 3,
            }}
          >
            <Icon
              icon={plusCircleIcon}
              size="sm"
              color="primary"
              sx={{ mt: 1 }}
            />
            <div>{consideration}</div>
          </li>
        ))}
      </ul>
    </div>
  )
}

const ImageUploadContainer = ({
  canSkip,
  consultationId,
  tempId,
  requireTelemedicineAgreement,
  onComplete,
}) => {
  const { t } = useTranslation()

  const {
    hasRequiredPhotos,
    skipUpload,
    isSkipUploadLoading,
    uploadImages,
    isUploadImagesLoading,
  } = usePhotos()

  const {
    mutateAsync: submitTelemedicineAgreement,
    loading: isSubmitTelemedicineAgreementLoading,
  } = useMutation(userClient.submitTelemedicineAgreement)

  const onSubmit = async (values) => {
    try {
      if (requireTelemedicineAgreement) {
        await submitTelemedicineAgreement(values)
      }

      await uploadImages({ consultationId, tempId, onSuccess: onComplete })
    } catch (error) {
      return { [FORM_ERROR]: getErrorMessage(error) }
    }
  }

  const onSkip = async () => {
    await skipUpload({ consultationId, tempId, onSuccess: onComplete })
  }

  return (
    <div sx={{ position: "relative" }}>
      <PhotoSlots
        sx={{
          mx: [-4, 0],
          mb: [5, 6],
          px: [4, 6],
          py: [5, 6],
          backgroundColor: "lightBlue",
        }}
        heading={t("PhotosUpload.ImageUploadContainer.title")}
        showDescription
      />

      <div>
        <div
          sx={{
            fontSize: 3,
            mb: 6,
          }}
        >
          {t("PhotosUpload.ImageUploadContainer.description")}
        </div>
        {requireTelemedicineAgreement ? (
          <TelemedicineAgreementForm
            onSubmit={onSubmit}
            isLoading={
              isSubmitTelemedicineAgreementLoading || isUploadImagesLoading
            }
            isDisabled={!hasRequiredPhotos}
          />
        ) : (
          <UploadPhotosButton
            isLoading={isUploadImagesLoading}
            isDisabled={!hasRequiredPhotos}
            onClick={onSubmit}
            sx={{ variant: "vwo.button.primary" }}
          />
        )}
        {canSkip && (
          <Button
            data-testid="web-portal-upload-photos-skip-btn"
            variant="link"
            isFullWidth
            onClick={onSkip}
            disabled={isSkipUploadLoading}
            sx={{ mt: 4 }}
          >
            <Trans i18nKey="PhotosUpload.ImageUploadContainer.skipButton" />
          </Button>
        )}
      </div>
    </div>
  )
}

const PhotosUpload = (props) => (
  <Fragment>
    <div
      sx={{
        display: "grid",
        gridTemplateColumns: [null, "7fr 5fr", "1fr 1fr"],
        alignItems: "center",
        mt: [5, 9],
        mb: [6, 8],
        bg: [null, "blue"],
      }}
    >
      <div
        sx={{
          py: [null, 6, 7],
          px: [null, 7],
          fontSize: 3,
          variant: "vwo.heading",
        }}
      >
        <Trans i18nKey="PhotosUpload.title" />
      </div>

      <StaticImage
        src="../../images/girl-ipad.jpg"
        alt=""
        sx={{ display: ["none", "block"], maxWidth: 600 }}
      />
    </div>
    <div
      sx={{
        display: ["flex", "grid"],
        flexDirection: ["column", null],
        gridTemplateColumns: [null, "auto", "1fr 2fr"],
        gap: [null, 7],
        mb: [7, 10],
      }}
    >
      <Considerations />

      <PhotosProvider>
        <ImageUploadContainer {...props} />
      </PhotosProvider>
    </div>
  </Fragment>
)

export default PhotosUpload
